import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import authService from '@services/authService';
import communityService from '@services/communityService';
import { navigationConfig } from '@config/navigation';
import useOnScroll from '@hooks/useOnScroll';
import { cn } from '@/lib/utils';
import soranLogo from '/assets/logos/soranlogo.jpg';

const headerVariants = {
  top: {
    backgroundColor: 'rgba(255, 255, 255, 0)',
    boxShadow: 'none',
    backdropFilter: 'blur(0px)',
  },
  scrolled: {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    backdropFilter: 'none',
  },
};

const containerVariants = {
  top: { height: '8rem' }, // h-32
  scrolled: { height: '5rem' }, // h-20
};

const logoVariants = {
  top: { height: '7rem' }, // h-28
  scrolled: { height: '4rem' }, // h-16
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const isScrolled = useOnScroll(20);

  // Check authentication and admin status
  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = authService.isAuthenticated();
      setIsAuthenticated(isAuth);
      
      // Check admin status if authenticated
      if (isAuth) {
        try {
          const adminStatus = await communityService.isAdmin();
          setIsAdmin(adminStatus);
        } catch (err) {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    
    void checkAuth();
    
    // Listen for storage events (for logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken') {
        void checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navLinks = navigationConfig.filter(n => n.showInMainNav);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <motion.header
      className="sticky top-0 z-50 w-full"
      variants={headerVariants}
      animate={isScrolled ? 'scrolled' : 'top'}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
    >
      <motion.div
        className="container flex items-center justify-between"
        variants={containerVariants}
        animate={isScrolled ? 'scrolled' : 'top'}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group" aria-label="Go to home">
          <motion.img
            src={soranLogo}
            alt="SORAN Foundation Logo"
            className="w-auto"
            variants={logoVariants}
            animate={isScrolled ? 'scrolled' : 'top'}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            style={{
              filter: 'brightness(1.1) contrast(1.1)',
            }}
            whileHover={{ scale: 1.05 }}
          />
          <span className="sr-only">SORAN Kids Foundation</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6" aria-label="Primary">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                'text-sm font-medium transition-colors',
                isScrolled
                  ? 'text-gray-700 hover:text-gray-900'
                  : 'text-secondary-dark hover:text-primary',
                location.pathname === link.path && 'font-semibold',
                location.pathname === link.path && (isScrolled ? 'text-gray-900' : 'text-primary')
              )}
              aria-current={location.pathname === link.path ? 'page' : undefined}
            >
              {link.title}
            </Link>
          ))}

          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              {isAdmin && (
                <Link
                  to="/admin/manage-admins"
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all hover:scale-105',
                    isScrolled
                      ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      : 'bg-purple-500 text-white hover:bg-purple-600 shadow-md'
                  )}
                  title="Admin Panel"
                >
                  <Shield className="h-4 w-4" />
                  <span className="hidden lg:inline">Admin Panel</span>
                </Link>
              )}
              <Link
                to="/parent-portal"
                className={cn('btn-primary-sm', isScrolled && 'btn-secondary-sm')}
              >
                Portal
              </Link>
            </div>
          ) : (
            <Link
              to="/login"
              className={cn('btn-primary-sm', isScrolled && 'btn-secondary-sm')}
            >
              Login
            </Link>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={cn(
              'h-6 w-6 transition-colors',
              isScrolled ? 'text-gray-700' : 'text-secondary-dark'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {isMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </motion.div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <motion.div
          id="mobile-menu"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="md:hidden bg-white shadow-lg"
        >
          <div className="container py-4">
            <nav className="flex flex-col space-y-4" aria-label="Mobile Primary">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : 'text-secondary hover:text-primary'
                  }`}
                  aria-current={location.pathname === link.path ? 'page' : undefined}
                >
                  {link.title}
                </Link>
              ))}

              {isAuthenticated ? (
                <div className="flex flex-col gap-3">
                  {isAdmin && (
                    <Link
                      to="/admin/manage-admins"
                      className="flex items-center justify-center gap-2 rounded-lg bg-purple-100 px-4 py-2 text-sm font-semibold text-purple-700 transition-all hover:bg-purple-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Shield className="h-4 w-4" />
                      Admin Panel
                    </Link>
                  )}
                  <Link
                    to="/parent-portal"
                    className="btn-primary-sm w-full text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Portal
                  </Link>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="btn-primary-sm w-full text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
              )}
            </nav>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;